// The inspection summary of a handover draft (H-Next): what was checked, what is open, what awaits
// reinspection and what is missing, each with where to go to complete it. Computed from the draft
// as edited (saved or not); nothing here is a signature or a final closing.
import {
  type HandoverDocument,
  INSPECTION_STATUS_LABELS,
  groupInspection,
  isComplete,
  progressOf,
} from '@sivan/shared';
import type { Draft } from './handover-draft';

/** Where an incomplete item is, in the editor. */
export type ReviewTarget =
  | { readonly kind: 'room'; readonly areaId: string }
  | { readonly kind: 'check'; readonly id: string }
  | { readonly kind: 'reinspection'; readonly findingId: string }
  | { readonly kind: 'section'; readonly section: 'checks' | 'reinspection' | 'findings' }
  | { readonly kind: 'date' };

export interface ReviewEntry {
  readonly key: string;
  readonly text: string;
  readonly target: ReviewTarget;
}

export interface Review {
  /** Rooms of the checklist with their progress. */
  readonly rooms: readonly {
    areaId: string;
    area: string;
    checked: number;
    total: number;
    defects: number;
  }[];
  readonly checks: { readonly checked: number; readonly total: number };
  /** Findings of this handover that are open. */
  readonly openFindings: number;
  /** Final handover: initial findings not reinspected yet, and those still defective. */
  readonly awaiting: readonly ReviewEntry[];
  readonly stillDefective: number;
  /** Items without a result (rooms, general checks). */
  readonly incomplete: readonly ReviewEntry[];
  /** Required or expected information that is missing. */
  readonly missing: readonly ReviewEntry[];
  /** Everything has a result and nothing is missing (open findings may remain). */
  readonly complete: boolean;
}

export function reviewOf(doc: HandoverDocument, draft: Draft): Review {
  const items = doc.inspection.items.map((i) => ({
    ...i,
    status: draft.results[i.id] ?? i.status,
  }));
  const rooms = groupInspection(items).map((a) => {
    const p = progressOf(a.categories.flatMap((c) => c.items));
    return {
      areaId: a.areaId,
      area: a.area,
      checked: p.checked,
      total: p.total,
      defects: p.defects,
    };
  });
  const checks = doc.generalChecks.items.map((c) => ({
    ...c,
    ...(draft.checks[c.id] ?? { status: c.status, quantity: '', note: '' }),
  }));
  const reinspections = (doc.reinspection?.items ?? []).map((r) => ({
    finding: r.finding,
    ...(draft.reinspections[r.finding.id] ?? {
      result: r.result,
      inspectedOn: '',
      note: '',
      photos: [],
    }),
  }));
  const place = (r: { finding: { area: string; component: string } }) =>
    `${r.finding.area} · ${r.finding.component}`;

  const incomplete: ReviewEntry[] = [
    ...rooms
      .filter((r) => !isComplete(r))
      .map((r) => ({
        key: `room:${r.areaId}`,
        text: `${r.area}: נבדקו ${String(r.checked)} מתוך ${String(r.total)}`,
        target: { kind: 'room' as const, areaId: r.areaId },
      })),
    ...checks
      .filter((c) => c.status === 'unchecked')
      .map((c) => ({
        key: `check:${c.id}`,
        text: `${c.name}: ${INSPECTION_STATUS_LABELS.unchecked}`,
        target: { kind: 'check' as const, id: c.id },
      })),
  ];

  const missing: ReviewEntry[] = [];
  if (draft.handoverDate === '')
    missing.push({ key: 'date', text: 'לא נקבע תאריך מסירה.', target: { kind: 'date' } });
  if (doc.generalChecks.items.length === 0)
    missing.push({
      key: 'checks',
      text: 'אין למסירה זו בדיקות ומסירה כללית.',
      target: { kind: 'section', section: 'checks' },
    });
  for (const c of checks) {
    if (c.status === 'defect' && c.note.trim() === '')
      missing.push({
        key: `check-note:${c.id}`,
        text: `${c.name}: סומן "לא תקין" בלי הערה.`,
        target: { kind: 'check', id: c.id },
      });
    if (c.quantityUnit !== null && c.status === 'ok' && c.quantity.trim() === '')
      missing.push({
        key: `check-quantity:${c.id}`,
        text: `${c.name}: חסרה כמות.`,
        target: { kind: 'check', id: c.id },
      });
  }
  if (doc.reinspection !== null && doc.reinspection.initial === null)
    missing.push({
      key: 'link',
      text:
        doc.reinspection.linkable === null
          ? 'אין מסירה ראשונית לדירה: אין ליקויים לבדיקה חוזרת.'
          : 'המסירה אינה מקושרת למסירה הראשונית, ולכן ליקוייה אינם מוצגים לבדיקה חוזרת.',
      target: { kind: 'section', section: 'reinspection' },
    });
  for (const r of reinspections) {
    if (r.result === 'still_defective' && r.note.trim() === '')
      missing.push({
        key: `reinspection-note:${r.finding.id}`,
        text: `${place(r)}: "עדיין לא תקין" בלי הערה.`,
        target: { kind: 'reinspection', findingId: r.finding.id },
      });
  }

  const awaiting = reinspections
    .filter((r) => r.result === 'pending')
    .map((r) => ({
      key: `reinspection:${r.finding.id}`,
      text: `${place(r)}: ${r.finding.description}`,
      target: { kind: 'reinspection' as const, findingId: r.finding.id },
    }));
  const progress = progressOf(checks);
  return {
    rooms,
    checks: { checked: progress.checked, total: progress.total },
    openFindings: draft.findings.filter((f) => f.status === 'open').length,
    awaiting,
    stillDefective: reinspections.filter((r) => r.result === 'still_defective').length,
    incomplete,
    missing,
    complete: incomplete.length === 0 && missing.length === 0 && awaiting.length === 0,
  };
}
